import { useEffect, useRef, useState } from 'react';
import { Button, Icon, Sheet } from '@astral/ui';
import type { PublishJobDTO, PublishState } from '@astral/shared';
import { useT } from '../providers/I18nProvider.js';

/**
 * Ease a displayed percentage toward the real backend value so a fast job that
 * jumps forward animates smoothly instead of teleporting. NEVER shows more than
 * the real target (progress is always truthful); the animation only lags behind.
 */
function useSmoothProgress(target: number): number {
  const [display, setDisplay] = useState(target);
  const ref = useRef(target);
  const raf = useRef(0);
  useEffect(() => {
    const canRaf = typeof requestAnimationFrame === 'function';
    const step = (): void => {
      const diff = target - ref.current;
      if (Math.abs(diff) < 0.5) {
        ref.current = target;
        setDisplay(target);
        return;
      }
      ref.current += diff * 0.18; // ~300ms settle
      setDisplay(ref.current);
      if (canRaf) raf.current = requestAnimationFrame(step);
    };
    if (canRaf) {
      cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(step);
    } else {
      setDisplay(target);
    }
    return () => {
      if (canRaf) cancelAnimationFrame(raf.current);
    };
  }, [target]);
  return display;
}

export interface PublishProgressSheetProps {
  open: boolean;
  job: PublishJobDTO | null;
  title: string;
  onRetry: () => void;
  onOpenPack: (url: string) => void;
  onCopy: (url: string) => void;
  onClose: () => void;
}

type PublishStep = 'PREPARING' | 'CREATING_SET' | 'UPLOADING' | 'FINALIZING';
const STEP_ORDER: PublishStep[] = ['PREPARING', 'CREATING_SET', 'UPLOADING', 'FINALIZING'];

/** Rank a state for comparing progress (COMPLETED ranks above all steps). */
function rank(state: PublishState): number {
  if (state === 'COMPLETED' || state === 'FINALIZING') return state === 'COMPLETED' ? STEP_ORDER.length : 3;
  const i = STEP_ORDER.indexOf(state as PublishStep);
  return i < 0 ? 0 : i;
}

/**
 * Non-blocking publish progress: shows real per-phase progress from the backend
 * job, then a polished success state or an honest failure with retry. The editor
 * behind it stays interactive.
 */
export function PublishProgressSheet({
  open,
  job,
  title,
  onRetry,
  onOpenPack,
  onCopy,
  onClose,
}: PublishProgressSheetProps): JSX.Element {
  const t = useT();
  const state = job?.state ?? 'PREPARING';
  const pct = Math.round(useSmoothProgress(job?.percentage ?? 0));
  const failed = state === 'FAILED';
  const done = state === 'COMPLETED';

  return (
    <Sheet open={open} onClose={done || failed ? onClose : () => {}} title={t('publish.progressTitle')}>
      {done && job?.result ? (
        <div className="publish-done">
          <div className="publish-done__badge">
            <Icon name="sparkles" size={28} />
          </div>
          <h3 className="publish-done__title">{t('publish.ready')}</h3>
          <p className="publish-done__meta">{title}</p>
          <p className="muted" style={{ fontSize: 13 }}>
            {t('stickers.count', { count: job.result.count })}
          </p>
          <div className="publish-done__url">{job.result.url}</div>
          <div className="stack" style={{ gap: 8, width: '100%' }}>
            <Button
              fullWidth
              size="lg"
              leftIcon={<Icon name="sparkles" size={18} />}
              onClick={() => onOpenPack(job.result!.url)}
            >
              {t('publish.open')}
            </Button>
            <div className="row-between" style={{ gap: 8 }}>
              <Button fullWidth variant="secondary" leftIcon={<Icon name="copy" size={16} />} onClick={() => onCopy(job.result!.url)}>
                {t('publish.copyLink')}
              </Button>
              <Button fullWidth variant="ghost" onClick={onClose}>
                {t('common.done')}
              </Button>
            </div>
          </div>
        </div>
      ) : failed ? (
        <div className="stack" style={{ gap: 14 }}>
          <div className="publish-fail">
            <Icon name="close" size={22} />
            <div>
              <div className="publish-fail__title">{t('publish.failed')}</div>
              <div className="muted" style={{ fontSize: 13 }}>
                {t('publish.failedAt', { phase: t(`publish.phase.${jobFailedPhase(job ?? ({ processedStickers: 0, totalStickers: 0 } as PublishJobDTO))}`) })}
              </div>
            </div>
          </div>
          {job?.error && <p className="publish-fail__error">{job.error}</p>}
          <div className="stack" style={{ gap: 8 }}>
            <Button fullWidth size="lg" leftIcon={<Icon name="history" size={18} />} onClick={onRetry}>
              {t('publish.retry')}
            </Button>
            <Button fullWidth variant="ghost" onClick={onClose}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="stack" style={{ gap: 16 }}>
          <div className="publish-progress__pct">{pct}%</div>
          <div className="publish-progress__track">
            <span className="publish-progress__fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="publish-steps">
            {STEP_ORDER.map((step) => {
              const r = rank(state);
              const sr = rank(step);
              const status = r > sr ? 'done' : r === sr ? 'active' : 'pending';
              const showCount = step === 'UPLOADING' && status !== 'pending' && (job?.totalStickers ?? 0) > 0;
              return (
                <div key={step} className={`publish-step publish-step--${status}`}>
                  <span className="publish-step__icon">
                    {status === 'done' ? <Icon name="check" size={14} /> : status === 'active' ? '…' : ''}
                  </span>
                  <span className="publish-step__label">{t(`publish.phase.${step}`)}</span>
                  {showCount && (
                    <span className="publish-step__count">
                      {job?.processedStickers} / {job?.totalStickers}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Sheet>
  );
}

/** Best-effort guess of which phase failed, from how far progress got. */
function jobFailedPhase(job: PublishJobDTO): PublishStep {
  if (job.processedStickers === 0) return 'CREATING_SET';
  if (job.processedStickers < job.totalStickers) return 'UPLOADING';
  return 'FINALIZING';
}
